/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --cursor: pointer;
  --accent-color: var(--nve-sys-accent-secondary-background);
  --color: var(--nve-sys-interaction-field-color);
  --label-color: var(--nve-sys-interaction-field-color);
  --label-text-transform: none;
  --label-font-weight: var(--nve-ref-font-weight-medium);
  --label-font-size: var(--nve-ref-font-size-100);
  --control-width: 100%;
  --control-height: var(--nve-sys-interaction-field-height);
  --_label-width: var(--label-width, 180px);
  --nve-sys-interaction-state-base: var(--background, var(--nve-sys-layer-canvas-background));
  --opacity: 1;
  --filter: none;
  contain: content;
}

::slotted(*) {
  color-scheme: var(--nve-sys-color-scheme);
  font-family: inherit;
}

::slotted(label) {
  font-weight: var(--label-font-weight) !important;
  font-size: var(--label-font-size) !important;
  color: var(--label-color) !important;
  cursor: var(--cursor) !important;
  text-transform: var(--label-text-transform) !important;
  display: inline-block !important;
  line-height: 1em !important;
  align-self: center !important;
}

:host(:state(disabled)),
:host(:state(disabled)) ::slotted(nve-control-message:not([status])) {
  --cursor: not-allowed;
}

:host(:state(disabled)) {
  --opacity: var(--nve-ref-opacity-500);
}

:host(:state(invalid)) ::slotted(nve-control-message:not([error], [status])) {
  display: none;
}

:host([fit-content]) {
  --control-width: fit-content;
  --label-width: fit-content;
  --width: fit-content;
}

:host([nve-control]) ::slotted(input:focus),
:host([nve-control]) ::slotted(select:focus),
:host([nve-control]) ::slotted(textarea:focus),
:host([nve-control]:focus-within) [internal-host]:has(nve-icon-button:focus) [input],
[internal-host]:has(nve-icon-button) {
  outline: 0 !important;
}

@container style(--control-type: custom) {
  [input] {
    outline: 0 !important;
  }
}

::slotted(nve-button[nve-control]) {
  width: 100%;
}

[input] {
  cursor: var(--cursor);
  min-height: var(--control-height);
  opacity: var(--opacity);
  filter: var(--filter);
  grid-area: input;
  display: flex;
  line-height: 1;
}

:host([nve-control='inline']) {
  [internal-host] {
    min-height: var(--control-height);
  }

  [input] {
    min-height: min-content;
  }
}

slot {
  line-height: 1;
  width: 100%;
}

slot[name='label'] {
  grid-area: label;
  display: inline-flex;
  align-self: center;
}

slot[name='messages'] {
  grid-area: messages;
  display: inline-flex;
}

[name='messages']:not(:has-slotted),
[name='label']:not(:has-slotted) {
  display: none !important;
}

[internal-host] {
  display: grid;
  width: 100%;
  gap: var(--nve-ref-space-xs);
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto;
  grid-template-areas: 'input input input';
}

:host([nve-control='inline']) [internal-host] {
  align-items: center;
  grid-template-columns: auto 1fr 1fr;

  slot[name='label'],
  slot[name='messages'] {
    align-items: center;
  }

  & ::slotted(label) {
    font-weight: var(--nve-ref-font-weight-regular) !important;
    color: var(--color) !important;
    width: 100%;
  }

  slot[name='label'] {
    line-height: 1;
    width: 100%;
  }
}

:host([layout='horizontal']) [internal-host],
:host([layout='horizontal-inline']) [internal-host] {
  grid-template-columns: var(--_label-width) 1fr minmax(0, var(--_label-width));
}

/** block controls */
[internal-host] {
  &:has([name='label']:has-slotted) {
    grid-template-areas:
      'label label label'
      'input input input';
  }

  &:has([name='messages']:has-slotted) {
    grid-template-areas:
      'input input input'
      'messages messages messages';
  }

  &:has([name='label']:has-slotted):has([name='messages']:has-slotted) {
    grid-template-areas:
      'label label label'
      'input input input'
      'messages messages messages';
  }
}

:host([layout='horizontal']),
:host([layout='horizontal-inline']) {
  [internal-host] {
    &:has([name='messages']:has-slotted) {
      grid-template-areas:
        'label input input'
        '. messages messages';
    }

    &:not(:has([name='messages']:has-slotted)) {
      grid-template-areas: 'label input input';
    }
  }
}

/** inline controls */
:host([nve-control='inline']) {
  --control-height: auto;
  width: fit-content;

  [internal-host] {
    grid-template-areas: 'input input input';

    &:has([name='label']:has-slotted) {
      grid-template-areas: 'input label label';
    }

    &:has([name='messages']:has-slotted) {
      grid-template-areas:
        'input input input'
        'messages messages messages';
    }

    &:has([name='label']:has-slotted):has([name='messages']:has-slotted) {
      grid-template-areas:
        'input label label'
        'messages messages messages';
    }
  }
}
